import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BookOpen, PiggyBank, Receipt, CalendarClock, Gift, NotebookPen, ArrowLeft, Settings2, UserCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, Loader } from "@/components/common";
import { MemoriesSection } from "@/components/space/MemoriesSection";
import { SavingsSection } from "@/components/space/SavingsSection";
import { BillsSection } from "@/components/space/BillsSection";
import { DatesSection } from "@/components/space/DatesSection";
import { NotesSection } from "@/components/space/NotesSection";
import { WishlistView } from "@/components/WishlistView";
import { ConnectionSettings } from "@/components/ConnectionSettings";

const SECTIONS = [
  { id: "story", label: "Our Story", icon: BookOpen },
  { id: "savings", label: "Savings", icon: PiggyBank },
  { id: "bills", label: "Bills", icon: Receipt },
  { id: "dates", label: "Dates", icon: CalendarClock },
  { id: "wishlist", label: "Wishlist", icon: Gift },
  { id: "notes", label: "Notes", icon: NotebookPen },
];

export default function ConnectionSpace() {
  const { id } = useParams();
  const nav = useNavigate();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("story");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/connections/${id}`);
      setConn(data);
    } catch {
      nav("/connections");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <Loader />;
  if (!conn) return null;

  return (
    <div className="iks-fade-up">
      <button onClick={() => nav("/connections")} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-sm mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> Connections
      </button>

      <div className="iks-card p-5 mb-5 flex items-center gap-4">
        <Avatar photo={conn.person.profile_photo} name={conn.person.name} size={56} />
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-xl text-slate-50 truncate">{conn.person.name}</h1>
          <p className="text-slate-500 text-sm">@{conn.person.username}</p>
        </div>
        <button onClick={() => nav(`/connections/${id}/profile`)} className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300" data-testid="view-profile-btn" title="View their profile">
          <UserCircle size={20} />
        </button>
        <button onClick={() => setSettingsOpen(true)} className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300" data-testid="connection-settings-btn" title="Connection settings">
          <Settings2 size={20} />
        </button>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = section === s.id;
          return (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`section-${s.id}`}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-900/60 text-slate-400 hover:text-slate-200"}`}>
              <Icon size={16} /> {s.label}
            </button>
          );
        })}
      </div>

      {section === "story" && <MemoriesSection cid={id} />}
      {section === "savings" && <SavingsSection cid={id} />}
      {section === "bills" && <BillsSection cid={id} />}
      {section === "dates" && <DatesSection cid={id} />}
      {section === "wishlist" && <WishlistView connectionId={id} shared />}
      {section === "notes" && <NotesSection cid={id} />}

      <ConnectionSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        conn={conn}
        onChanged={load}
        onRemoved={() => nav("/connections")}
      />
    </div>
  );
}
